/**
 * 批量选券
 */
import React, { useEffect, useRef } from 'react';
import { Button } from 'antd';
import intl from 'react-intl-universal';

import { SourceChannelType, OccType } from '@/config/constant';
import useSafeState from '@/hooks/useSafeState';
import { fetchUserCouponCount } from '@/api/guest';
import DateJS, { format2TimeStamp } from '@/utils/date';
import { parseFen2Yuan, parse2Number, parseYuan2Fen } from '@/utils/number';
import { getEmptyCouponData } from '@/redux/actions/reservation/helper';
import { CouponValue } from '@/redux/reducers/reservation/base.d';

import BatchCouponModal from '@/components/BatchCouponModal';

// import styles from './index.module.scss';

export interface IBatchCouponParams {
  extra: {
    wyuid: string;
    // 换房时传
    couponNos?: string[];
  };
  rooms: {
    indexId: string;
    roomId: number;
    roomNo: string;
    roomTypeId: number;
    roomTypeName: string;
    roomPriceList: {
      key: string;
      occType: OccType;
      rentDate: string;
      roomPrice: string;
      couponNo?: string;
    }[];
  }[];
}

interface IProps {
  userId: string;
  channelType: SourceChannelType;
  getCouponParam: () => Promise<IBatchCouponParams>;
  onChange: (data: Record<string, Record<string, CouponValue>>) => void;
}

const Index: React.FC<IProps> = (props) => {
  const { userId, channelType, getCouponParam, onChange } = props;
  const [couponCount, setCount] = useSafeState(0);
  const batchCouponModalRef = useRef();

  // 线下渠道 有券可选
  const disabled = channelType !== SourceChannelType.WALK_IN || couponCount === 0;

  // 修改
  const handleSelectChange = (data) => {
    const roomList = data.priceRooms || [];
    if (roomList.length) {
      // 房间-日期-券
      const roomCouponMap: Record<string, Record<string, CouponValue>> = {};
      roomList.forEach((room) => {
        (room.datePrices || []).forEach((dayPrice) => {
          const { key, couponNo, couponAmount, couponWyuid } = dayPrice;
          // 有券 但uid为空 剔除数据
          if (couponNo && !couponWyuid) return;
          const [roomItemKey, dateItemKey] = key.split('::');
          // 日期-券
          const dateCouponMap = roomCouponMap[roomItemKey] || {};
          dateCouponMap[dateItemKey] = couponNo
            ? {
                couponValue: parseFen2Yuan(couponAmount),
                couponAmount,
                couponName: dayPrice.couponName,
                couponNo,
                couponType: dayPrice.couponType,
                couponWyuid: dayPrice.couponWyuid,
                couponSubType: dayPrice.couponSubType,
              }
            : getEmptyCouponData();
          roomCouponMap[roomItemKey] = dateCouponMap;
        });
      });
      onChange(roomCouponMap);
    }
  };

  // 入参
  const parseRoomParams = (roomList: IBatchCouponParams['rooms']) => {
    let startTimeVal = Number.MAX_SAFE_INTEGER; // 订单开始时间
    let endTimeVal = 0; // 订单结束时间

    const priceRooms = roomList.map((room) => {
      const roomItemKey = room.indexId;
      return {
        ...room,
        datePrices: room.roomPriceList.map((dayPrice) => {
          const timeVal = format2TimeStamp(dayPrice.rentDate);
          if (timeVal < startTimeVal) {
            // 当前时间早于最早时间
            startTimeVal = timeVal;
          }
          if (timeVal > endTimeVal) {
            // 当前日期晚于最晚时间
            endTimeVal = timeVal;
          }
          return {
            ...dayPrice,
            // 房间key::日期key
            key: `${roomItemKey}::${dayPrice.key}`,
            roomPrice: parse2Number(parseYuan2Fen(dayPrice.roomPrice)) || 0,
          };
        }),
        indexId: undefined,
        roomPriceList: undefined,
      };
    });

    return {
      // 入住日期范围 可用券房间为空时默认当前时间
      dateStrRange:
        endTimeVal === 0 ? [DateJS(), DateJS()] : [DateJS(startTimeVal), DateJS(endTimeVal)],
      // 入住房间列表
      priceRooms,
    };
  };

  // 点击批量选券
  const handleTriggerClick = () => {
    if (disabled) return;
    const inst: any = batchCouponModalRef.current;
    if (inst) {
      //
      getCouponParam().then((param) => {
        const roomList = param.rooms || [];
        if (roomList.length) {
          inst.openModal({
            ...param,
            ...parseRoomParams(roomList),
            onConfirm: handleSelectChange,
            onReset: handleSelectChange,
          });
        }
      });
    }
  };

  useEffect(() => {
    setCount(0);
    if (userId) {
      fetchUserCouponCount({ wyuid: userId }).then((res) => {
        if (res.data) {
          setCount(res.data.currentShopAvailQty || 0);
        }
      });
    }
  }, [userId]);

  return (
    <>
      <Button disabled={disabled} onClick={handleTriggerClick}>
        {intl.get('reservation_field_room_batch_coupon_btn')}
      </Button>
      <BatchCouponModal ref={batchCouponModalRef} />
    </>
  );
};

export default Index;
